import { toPng } from "html-to-image";
import Cookies from "js-cookie";
import jsPDF from "jspdf";
import moment from "moment";
import { toast } from "react-toastify";
import { DiscountType } from "../redux/features/event/event.types";
import { adminAuthTokenCookieKey, clientAuthTokenCookieKey, FEE_PERCENTAGE } from "./constants";

export const setCookie = (name: string, value: string): void => {
  Cookies.set(name, value, { expires: 1, path: "/" });
};

export const getCookie = (name: string): string | undefined => {
  const value = Cookies.get(name);
  return value;
};

export const removeCookie = (name: string): void => {
  Cookies.remove(name);
};

export const handleError = (error: any, toastError: boolean = false) => {
  if (toastError) {
    toast.error(error?.error?.data?.message ?? "An error occurred");
  }
};

export const isClientAuthCookieSet = (): boolean => {
  const authCookie = getCookie(clientAuthTokenCookieKey);

  return authCookie !== undefined;
};

export const isAdminAuthCookieSet = (): boolean => {
  const authCookie = getCookie(adminAuthTokenCookieKey);

  return authCookie !== undefined;
};

export const formatDate = (
  date: string | Date | moment.Moment | undefined | null,
  format: string = "MMMM DD, YYYY",
): string => {
  try {
    if (!date) {
      return "";
    }
    const momentDate = moment(date);
    if (!momentDate.isValid()) {
      throw new Error("Invalid date");
    }
    return momentDate.format(format);
  } catch (error) {
    return "Invalid date";
  }
};

export const formatTime = (
  time: string | Date | moment.Moment | undefined | null,
  format: string = "hh:mm A",
): string => {
  try {
    if (!time) {
      return "";
    }
    const momentTime = moment(time);
    if (!momentTime.isValid()) {
      throw new Error("Invalid time");
    }
    return momentTime.format(format);
  } catch (error) {
    return "Invalid time";
  }
};

export const parseTimeToDate = (time: string): Date | null => {
  try {
    const momentTime = moment(time, "hh:mm A");
    if (!momentTime.isValid()) {
      throw new Error("Invalid time format");
    }
    return momentTime.toDate();
  } catch (error) {
    return null;
  }
};

export const parseDateStringToDate = (dateString: string): Date | null => {
  try {
    const momentDate = moment(dateString);
    if (!momentDate.isValid()) {
      throw new Error("Invalid date format");
    }
    return momentDate.toDate();
  } catch (error) {
    return null;
  }
};

export const formatToNaira = (amount: number | string) => {
  const numericAmount = Number(String(amount).replace(/[^\d.-]/g, "") ?? 0);

  if (isNaN(numericAmount)) {
    return "Invalid amount"; // Return a default message or handle the error as needed
  }

  const formatter = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(numericAmount);
};

export const formatNumber = (amount: number | string) => {
  if (amount === Infinity) {
    return "∞";
  }

  const numericAmount = Number(String(amount).replace(/[^\d.-]/g, "") ?? 0);

  if (isNaN(numericAmount)) {
    return "Invalid amount"; // Return a default message or handle the error as needed
  }

  return numericAmount.toLocaleString();
};

export const formatPercentage = (amount: number | string) => {
  if (amount === Infinity) {
    return "∞";
  }

  const numericAmount = Number(String(amount).replace(/[^\d.-]/g, "") ?? 0);

  if (isNaN(numericAmount)) {
    return "Invalid amount"; // Return a default message or handle the error as needed
  }

  return numericAmount.toLocaleString() + "%";
};

export const parseNairaToNumber = (
  nairaString: string | number,
): number | null => {
  try {
    const numericString =
      String(nairaString).replace(/[^\d.-]/g, "") !== ""
        ? String(nairaString).replace(/[^\d.-]/g, "")
        : "0";

    const numericValue = parseFloat(numericString);

    if (isNaN(numericValue)) {
      throw new Error("Invalid naira format");
    }

    return numericValue;
  } catch (error) {
    return null;
  }
};

export const removeElementAtIndex = <T>(
  arrayOriginal: T[],
  index: number,
): T[] => {
  const array = JSON.parse(JSON.stringify(arrayOriginal));
  // Check if the index is within bounds
  if (index >= 0 && index < array.length) {
    array.splice(index, 1);
  }
  return array;
};

export const deepCopy = (item: any) => {
  return JSON.parse(JSON.stringify(item));
};

export const moveElement = <T>(
  array: T[],
  startIndex: number,
  endIndex: number,
): T[] => {
  const arrayCopy = JSON.parse(JSON.stringify(array));
  const [element] = arrayCopy.splice(startIndex, 1);
  arrayCopy.splice(endIndex, 0, element);
  return arrayCopy;
};

export const copyTextToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error("Failed to copy text to clipboard", error);
  }
};

export const getEventUrl = (slug: string) => {
  return `${window.location.origin}/events/${slug}`;
};

export const getTimeAgo = (timeString: string): string => {
  const time = new Date(timeString).getTime();
  const now = Date.now();
  const secondsAgo = Math.floor((now - time) / 1000);

  if (secondsAgo < 60) {
    return `${secondsAgo} seconds ago`;
  }

  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) {
    return `${minutesAgo} minutes ago`;
  }

  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) {
    return `${hoursAgo} hours ago`;
  }

  const daysAgo = Math.floor(hoursAgo / 24);
  return `${daysAgo} days ago`;
};

export const getTimeToGo = (timeString: string): string => {
  const time = new Date(timeString).getTime();
  const now = Date.now();

  const secondsToGo = Math.floor((time - now) / 1000);
  if (secondsToGo < 0) {
    return "past";
  }

  if (secondsToGo < 60) {
    return `${secondsToGo} seconds`;
  }

  const minutesToGo = Math.floor(secondsToGo / 60);
  if (minutesToGo < 60) {
    return `${minutesToGo} minutes`;
  }

  const hoursToGo = Math.floor(minutesToGo / 60);
  if (hoursToGo < 24) {
    return `${hoursToGo} hours`;
  }

  const daysTogo = Math.floor(hoursToGo / 24);
  return `${daysTogo} days`;
};

export const calcFees = (
  total: number,
  discountValue?: number,
  discountType?: DiscountType,
) => {
  let totalTicketPrice = total;

  if (discountValue && discountType) {
    if (discountType === "percentage") {
      totalTicketPrice =
        totalTicketPrice - (totalTicketPrice * discountValue) / 100;
    } else if (discountType === "amount") {
      totalTicketPrice = totalTicketPrice - discountValue;
    }
  }

  const totalFees =
    totalTicketPrice * FEE_PERCENTAGE + (totalTicketPrice > 0 ? 100 : 0);

  return totalFees > 0 ? totalFees : 0;
};

export const calcTotalTicketPrice = (
  total: number,
  discountValue?: number,
  discountType?: DiscountType,
  addFees: boolean = true,
) => {
  let totalTicketPrice = total;

  if (discountValue && discountType) {
    if (discountType === "percentage") {
      totalTicketPrice =
        totalTicketPrice - (totalTicketPrice * discountValue) / 100;
    } else if (discountType === "amount") {
      totalTicketPrice = totalTicketPrice - discountValue;
    }
  }

  totalTicketPrice = addFees
    ? totalTicketPrice +
      (totalTicketPrice * FEE_PERCENTAGE + (totalTicketPrice > 0 ? 100 : 0))
    : totalTicketPrice;

  return totalTicketPrice > 0 ? totalTicketPrice : 0;
};

export const regexText = (str: string, regexArray: RegExp[]): boolean => {
  return regexArray.some((regex) => regex.test(str));
};

export const getRandomColor = (
  colors: string[],
  selectedColors: string[],
  mode: "sequential" | "random" = "sequential",
): string => {
  if (colors.length === 0) {
    throw new Error("Colors array cannot be empty");
  }

  let randomColor: string;

  if (mode === "random") {
    do {
      randomColor =
        colors[Math.floor(Math.random() * (colors.length - 1 - 0 + 1)) + 0];
    } while (selectedColors.includes(randomColor));
  } else {
    const index = selectedColors.length % colors.length;
    randomColor = colors[index];
  }

  return randomColor;
};

export const handleDownloadPdf = async (
  ref: React.RefObject<HTMLElement | null>,
  docName: string,
) => {
  if (!ref) return;

  const original = ref.current;
  if (!original) return;

  // Clone the element
  const clone = original.cloneNode(true) as HTMLElement;

  clone.style.position = "static";
  clone.style.bottom = "auto";
  clone.style.left = "0";
  clone.style.right = "0";
  clone.style.margin = "0 auto";

  // Create off-screen container
  const offscreen = document.createElement("div");
  offscreen.style.width = "595px"; // Force desktop width
  offscreen.style.transform = "scale(1)";
  offscreen.style.transformOrigin = "top left";
  offscreen.style.position = "absolute";
  offscreen.style.top = "-9999px";
  offscreen.style.left = "-9999px";
  // offscreen.style.width = `${original.offsetWidth}px`;
  offscreen.appendChild(clone);

  document.body.appendChild(offscreen);

  try {
    const dataUrl = await toPng(clone, {
      cacheBust: true,
      width: 595,
      // height: clone.scrollHeight,
      backgroundColor: "#f4f7fa",
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    const img = new Image();
    img.src = dataUrl;

    img.onload = () => {
      pdf.setFillColor(244, 246, 250);
      pdf.rect(
        0,
        0,
        pdf.internal.pageSize.width,
        pdf.internal.pageSize.height,
        "F",
      );

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (img.height * pdfWidth) / img.width;

      pdf.addImage(img, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${docName}.pdf`);
    };
  } catch (error) {
    console.error("Error generating PDF:", error);
  } finally {
    document.body.removeChild(offscreen);
  }
};

export const handleShare = async (title: string, text: string, url: string) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title: title,
        text: text,
        url: url,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  } else {
    toast.success("Web Share API is not supported in your browser");
  }
};

export const formatDateTime = (date: string, time: string) => {
  // Get time values
  const parsedTime = parseTimeToDate(time)!;

  // Get date from string, this converts to local time...from utc which the date string comes in
  const mergedDate = parseDateStringToDate(date)!;

  // Include time values to date
  mergedDate.setHours(
    parsedTime.getHours(),
    parsedTime.getMinutes(),
    parsedTime?.getSeconds(),
    parsedTime?.getMilliseconds(),
  );

  return formatDate(mergedDate, "yyyy-MM-DD HH:mm:ss ZZZ");
};
